import config from '../config/api';
import { TradingWsEnvelope } from '../types/trading';
import {
  buildIntradaySignalStartCommand,
  buildIntradaySignalStopCommand,
  buildIntradaySignalTakeOverCommand,
  buildTradingSocketCommand,
  IntradaySignalStartCommandPayload,
  normalizeTradingSymbol,
  parseTradingWsEnvelope,
} from './tradingSocketProtocol';

type EventHandler = (message: TradingWsEnvelope<any>) => void;
type StatusHandler = (connected: boolean) => void;
export type TradingSocketMarketStream = 'sampled' | 'raw';

interface TradingSocketConnectOptions {
  marketStream?: TradingSocketMarketStream;
}

export class TradingSocket {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private openedAt: number | null = null;
  private readonly maxReconnectAttempts = 8;
  private reconnectTimer: number | null = null;
  private eventHandler: EventHandler | null = null;
  private statusHandler: StatusHandler | null = null;
  private userUuid = '';
  private symbols = new Set<string>();
  private marketStream: TradingSocketMarketStream | undefined;
  private intradaySignalConfig: IntradaySignalStartCommandPayload | null = null;
  private intentionalDisconnect = false;

  connect(
    userUuid: string,
    symbols: string[],
    onEvent: EventHandler,
    onStatus?: StatusHandler,
    options?: TradingSocketConnectOptions,
  ) {
    this.userUuid = userUuid;
    this.eventHandler = onEvent;
    this.statusHandler = onStatus ?? null;
    this.intentionalDisconnect = false;
    this.symbols = new Set(symbols.map((symbol) => normalizeTradingSymbol(symbol)).filter(Boolean));
    this.marketStream = options?.marketStream;

    const params = new URLSearchParams();
    params.set('user_uuid', userUuid);
    if (this.symbols.size > 0) {
      params.set('symbols', Array.from(this.symbols).join(','));
    }
    if (this.marketStream) {
      params.set('market_stream', this.marketStream);
    }

    const url = `${config.tradingWsUrl}?${params.toString()}`;
    try {
      this.socket = new WebSocket(url);
    } catch (error) {
      // Prevent hard crashes when env ws URL is malformed.
      this.statusHandler?.(false);
      return;
    }

    this.socket.onopen = () => {
      this.openedAt = Date.now();
      this.reconnectAttempts = 0;
      // Re-subscribe all tracked symbols — covers symbols added between
      // connect() and onopen, as well as initial symbols.
      this.flushSubscriptions();
      this.flushIntradaySignalSession();
      this.statusHandler?.(true);
    };

    this.socket.onmessage = (event) => {
      const parsed = parseTradingWsEnvelope(event.data);
      if (parsed) this.eventHandler?.(parsed);
    };

    this.socket.onclose = (event) => {
      // Close codes name the culprit: 1000 clean, 1001 tab hidden/navigated/slept,
      // 1006 abnormal (network or proxy dropped it, no close frame sent).
      const heldForSeconds = this.openedAt
        ? Math.round((Date.now() - this.openedAt) / 1000)
        : null;
      console.warn(
        `[trading-ws] closed code=${event.code} reason=${event.reason || "(none)"} ` +
        `wasClean=${event.wasClean} heldForSeconds=${heldForSeconds}`
      );
      this.openedAt = null;
      this.statusHandler?.(false);
      this.tryReconnect();
    };

    this.socket.onerror = (event) => {
      console.warn("[trading-ws] error before close", event);
      this.statusHandler?.(false);
    };
  }

  subscribe(symbol: string) {
    const normalized = normalizeTradingSymbol(symbol);
    if (!normalized) return;
    this.symbols.add(normalized);
    // If socket is open, send immediately. Otherwise the symbol is in
    // this.symbols and will be flushed when onopen fires.
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(buildTradingSocketCommand('subscribe', normalized));
    }
  }

  unsubscribe(symbol: string) {
    const normalized = normalizeTradingSymbol(symbol);
    if (!normalized) return;
    this.symbols.delete(normalized);
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(buildTradingSocketCommand('unsubscribe', normalized));
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;

    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      // Remove handlers before closing to prevent onclose from triggering reconnect
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.onopen = null;
      this.socket.close(1000, 'Client closed');
      this.socket = null;
    }

    this.intradaySignalConfig = null;
    this.marketStream = undefined;
    this.statusHandler?.(false);
  }

  startIntradaySignalTest(strategies: IntradaySignalStartCommandPayload) {
    const normalized = strategies
      .map((strategy) => ({ ...strategy, symbol: normalizeTradingSymbol(strategy.symbol) }))
      .filter((strategy) => strategy.symbol);
    if (normalized.length === 0) return;

    this.intradaySignalConfig = normalized;

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(buildIntradaySignalStartCommand(normalized));
    }
  }

  takeOverIntradaySignalTest(strategies: IntradaySignalStartCommandPayload) {
    const normalized = strategies
      .map((strategy) => ({ ...strategy, symbol: normalizeTradingSymbol(strategy.symbol) }))
      .filter((strategy) => strategy.symbol);
    if (normalized.length === 0) return;

    this.intradaySignalConfig = normalized;

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(buildIntradaySignalTakeOverCommand(normalized));
    }
  }

  stopIntradaySignalTest(strategyId?: string) {
    if (strategyId) {
      this.intradaySignalConfig =
        this.intradaySignalConfig?.filter((strategy) => strategy.strategy_id !== strategyId) ?? null;
      if (this.intradaySignalConfig?.length === 0) this.intradaySignalConfig = null;
    } else {
      this.intradaySignalConfig = null;
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(buildIntradaySignalStopCommand(strategyId));
    }
  }

  private tryReconnect() {
    if (this.intentionalDisconnect) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts || !this.userUuid) {
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    this.reconnectAttempts += 1;

    this.reconnectTimer = window.setTimeout(() => {
      this.connect(this.userUuid, Array.from(this.symbols), (event) => {
        this.eventHandler?.(event);
      }, this.statusHandler || undefined, { marketStream: this.marketStream });
    }, delay);
  }

  private send(payload: Record<string, unknown>) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const msgType = payload.type === 'unsubscribe' ? 'unsubscribe' : 'subscribe';
      const symbol = typeof payload.symbol === 'string' ? payload.symbol : '';
      this.socket.send(buildTradingSocketCommand(msgType, symbol));
    }
  }

  private flushSubscriptions() {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    this.symbols.forEach((symbol) => {
      this.socket?.send(buildTradingSocketCommand('subscribe', symbol));
    });
  }

  private flushIntradaySignalSession() {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!this.intradaySignalConfig || this.intradaySignalConfig.length === 0) {
      return;
    }

    this.socket.send(buildIntradaySignalStartCommand(this.intradaySignalConfig));
  }
}
